import { Directive, ElementRef, OnDestroy, inject } from '@angular/core';
import { DragDrop, DragRef, Point } from '@angular/cdk/drag-drop';

/**
 * 모달 헤더를 잡고 모달(.modal-content)을 이동한다.
 * - selector 가 .modal-header 이므로 이 디렉티브를 import 한 컴포넌트의 모달 헤더에 자동으로 적용된다. (NpxCommonModule)
 * - 전체화면(.modal-fullscreen)일 때는 이동하지 않는다.
 * - 헤더가 화면 밖으로 나가지 않게 이동 범위를 제한한다.
 */
@Directive({
    selector: '.modal-header',
    standalone: true,
    host: {
        '[class.np-modal-header-drag]': 'true'
    }
})
export class Bs5ModalHeaderDragDirective implements OnDestroy {

    private el: ElementRef<HTMLElement> = inject(ElementRef);
    private dragDrop = inject(DragDrop);

    private dragRef: DragRef | null = null;

    // 모달은 열린 뒤에 .modal-content 안으로 붙으므로, 처음 누를 때 이동 대상을 찾아 연결한다.
    // pointerdown 은 CDK 가 사용하는 mousedown / touchstart 보다 먼저 발생한다.
    private onPointerDown = (event: PointerEvent) => {
        const header = this.el.nativeElement;

        if (!this.dragRef) {
            const modalContent = header.closest<HTMLElement>('.modal-content');
            // 모달 밖에서 .modal-header 클래스를 쓴 경우는 제외
            if (!modalContent) {
                return;
            }
            // withRootElement 는 이동 대상(.modal-content) 전체에 누름 이벤트를 연결하므로, 잡는 곳을 헤더로 한정한다. (본문을 잡으면 이동하지 않게)
            this.dragRef = this.dragDrop.createDrag(header).withRootElement(modalContent).withHandles([header]);
            this.dragRef.constrainPosition = this.constrainPosition;
        }

        // 전체화면이거나, 기존 이동 버튼(cdkDragHandle)을 누른 경우는 이 디렉티브로 이동하지 않는다. (두 번 이동 방지)
        const fromDragHandle = !!(event.target as HTMLElement | null)?.closest('.cdk-drag-handle');
        this.dragRef.disabled = !!header.closest('.modal-fullscreen') || fromDragHandle;
    };

    // 헤더를 다시 잡을 수 있게 이동 범위를 제한한다. 헤더는 화면 위아래 안에, 좌우는 VISIBLE_MIN 만큼 보이게 둔다.
    // CDK 는 여기서 돌려준 값을 모달의 왼쪽 위 좌표(화면 기준)로 쓴다. (point 는 마우스 위치, pickup 은 누른 위치의 모달 안 좌표. 헤더는 모달 맨 위에 있다)
    private constrainPosition = (point: Point, dragRef: DragRef, dimensions: DOMRect, pickup: Point): Point => {
        const VISIBLE_MIN = 100;
        const headerHeight = this.el.nativeElement.offsetHeight;
        const left = point.x - pickup.x;
        const top = point.y - pickup.y;
        return {
            x: Math.min(Math.max(left, VISIBLE_MIN - dimensions.width), window.innerWidth - VISIBLE_MIN),
            y: Math.min(Math.max(top, 0), window.innerHeight - headerHeight)
        };
    };

    constructor() {
        this.el.nativeElement.addEventListener('pointerdown', this.onPointerDown, true);
    }

    ngOnDestroy(): void {
        this.el.nativeElement.removeEventListener('pointerdown', this.onPointerDown, true);
        this.dragRef?.dispose();
    }
}
